import { Injectable } from '@angular/core';
import { PropertyStateService } from '../state/property-state.service';
import { CalculatorService, ICalculatorField, ICalculatorSection } from './calculator.service';
import { CurrencyPipe } from '@angular/common';

@Injectable()
export class FlipAnalysisService {

    constructor(
        private state: PropertyStateService,
        private calculator: CalculatorService,
        private currencyPipe: CurrencyPipe
    ) {}

    init() {
        const state = this.state;

        this.calculator.setFieldValuesFromParseObject([state.flipAnalysis_sharedTopBox, state.flipAnalysis_cashBox, state.flipAnalysis_financeBox], state.saFlip);
    }

    calculate() {
        this.updateCashBox();
        this.updateFinanceBox();
    }

    // inputs are disabled on Flyer Full Analysis
    disableFormFields() {
        const state = this.state;

        [state.flipAnalysis_sharedTopBox, state.flipAnalysis_cashBox, state.flipAnalysis_financeBox].forEach((box: ICalculatorSection[]) => {
            box.forEach((section: ICalculatorSection) => {
                section.fields.forEach((field: ICalculatorField) => {
                    if (field.formControl) {
                        field.formControl.disable();
                    }
                });
            });
        });
    }

    enableFormFields() {
        const state = this.state;

        [state.flipAnalysis_sharedTopBox, state.flipAnalysis_cashBox, state.flipAnalysis_financeBox].forEach((box: ICalculatorSection[]) => {
            box.forEach((section: ICalculatorSection) => {
                section.fields.forEach((field: ICalculatorField) => {
                    if (field.formControl) {
                        field.formControl.enable();
                    }
                });
            });
        });
    }

    setAnnualTaxes() {
        const state = this.state;

        if (state.estatedInfo.has('taxes') && state.estatedInfo.get('taxes')[0] && state.estatedInfo.get('taxes')[0].amount) {
            const tax = state.estatedInfo.get('taxes')[0].amount;
            this.calculator.getField(state.flipAnalysis_cashBox, 'taxes').formControl?.setValue(this.currencyPipe.transform(tax));
            this.calculator.getField(state.flipAnalysis_cashBox, 'taxes').inputValue = Number(tax);
        }
    }

    updateCashBox() {
        const state = this.state;

        this.calculator.getField(state.flipAnalysis_cashBox, 'simo-cc').outputValue = this.calcSimoCC();
        this.calculator.getField(state.flipAnalysis_cashBox, 'acqui-cost').outputValue = this.calcAcquiCost();
        this.calculator.getField(state.flipAnalysis_cashBox, 'second-closing').outputValue = this.calcSecondClosing();
        this.calculator.getField(state.flipAnalysis_cashBox, 'less-commission').outputValue = this.calcLessCommission();
        this.calculator.getField(state.flipAnalysis_cashBox, 'dispo').outputValue = this.calcDispo();
        this.calculator.getField(state.flipAnalysis_cashBox, 'hoa-held').outputValue = this.calcHOAHeld();
        this.calculator.getField(state.flipAnalysis_cashBox, 'less-utils').outputValue = this.calcLessUtil();
        this.calculator.getField(state.flipAnalysis_cashBox, 'taxes-while-held').outputValue = this.calcPropTaxes();
        this.calculator.getField(state.flipAnalysis_cashBox, 'misc').outputValue = this.calcMisc();
        this.calculator.getField(state.flipAnalysis_cashBox, 'holding').outputValue = this.calcHoldingCost();
        this.calculator.getField(state.flipAnalysis_cashBox, 'total').outputValue = this.calcTotal();
        this.calculator.getField(state.flipAnalysis_cashBox, 'net-income').outputValue = this.calcNetIncome();
        this.calculator.getField(state.flipAnalysis_cashBox, 'roi').outputValue = this.calcROI();

        const insuranceField = this.calculator.getField(state.flipAnalysis_cashBox, 'less-insurance');
        if (!insuranceField.inputValue) {
            const estimate = this.calcInsuranceEstimateWhileHeld();
            insuranceField.formControl?.setValue(this.currencyPipe.transform(estimate));
        }
    }

    updateFinanceBox() {
        const state = this.state;

        this.calculator.getField(state.flipAnalysis_financeBox, 'down-payment').outputValue = this.calculateDownPayment();
        this.calculator.getField(state.flipAnalysis_financeBox, 'amount-financed').outputValue = this.calculateAmountFinanced();
        this.calculator.getField(state.flipAnalysis_financeBox, 'annual-loan-payment').outputValue = this.calculateAnnualLoanPayment();
        this.calculator.getField(state.flipAnalysis_financeBox, 'points-private').outputValue = this.calculateLoanPointsPrivate();
        this.calculator.getField(state.flipAnalysis_financeBox, 'monthly-loan-payment').outputValue = this.calculateMonthlyLoanPayment();
        this.calculator.getField(state.flipAnalysis_financeBox, 'total-payment').outputValue = this.calculateTotalPayment();
        this.calculator.getField(state.flipAnalysis_financeBox, 'holding-plus-loan').outputValue = this.calculateHoldingPlusLoan();
        this.calculator.getField(state.flipAnalysis_financeBox, 'total-cash').outputValue = this.calculateTotalCashOutOfPocket();
        this.calculator.getField(state.flipAnalysis_financeBox, 'net-income-after-debt').outputValue = this.calculateNetIncomeAfterDebt();
        this.calculator.getField(state.flipAnalysis_financeBox, 'cash-on-cash').outputValue = this.calculateCashOnCash();
    }

    calcSimoCC(): number {
        const purchasePrice = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'purchase-price').inputValue as number;
        const simoPercent = this.calculator.getField(this.state.flipAnalysis_cashBox, 'simo-cc').inputValue as number;
        return purchasePrice * simoPercent;
    }

    calcAcquiCost(): number {
        const purchasePrice = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'purchase-price').inputValue as number;
        const renoCost = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'reno-cost').inputValue as number;
        const simoCC = this.calcSimoCC();

        return purchasePrice + renoCost + simoCC;
    }

    calcSecondClosing(): number {
        return 0.02 * (this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'arv').inputValue as number);
    }

    calcLessCommission(): number {
        return 0.05 * (this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'arv').inputValue as number);
    }

    calcDispo(): number {
        return this.calcSecondClosing() + this.calcLessCommission();
    }

    calcHOAHeld(): number {
        const holdTime = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'hold-time').inputValue as number;
        const moHOA = this.calculator.getField(this.state.flipAnalysis_cashBox, 'mo-hoa').inputValue as number;
        return holdTime * moHOA;
    }

    calcLessUtil(): number {
        return 250 * (this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'hold-time').inputValue as number);
    }

    calcPropTaxes(): number {
        const taxes = this.calculator.getField(this.state.flipAnalysis_cashBox, 'taxes').inputValue as number;
        const holdTime = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'hold-time').inputValue as number;
        return (taxes / 12) * holdTime;
    }

    calcMisc(): number {
        return 0.08 * (this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'reno-cost').inputValue as number);
    }

    calcHoldingCost(): number {

        let insurance = this.calculator.getField(this.state.flipAnalysis_cashBox, 'less-insurance').inputValue as number;

        if(!insurance) {
            insurance = this.calcInsuranceEstimateWhileHeld();
        }

        const hoa = this.calcHOAHeld();
        const utils = this.calcLessUtil();
        const taxes = this.calcPropTaxes();
        const misc = this.calcMisc();

        return insurance + hoa + utils + taxes + misc;
    }

    calcTotal(): number {
        const acqui = this.calcAcquiCost();
        const dispo = this.calcDispo();
        const holding = this.calcHoldingCost();

        return acqui + dispo + holding;
    }

    calcNetIncome(): number {
        return (this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'arv').inputValue as number) - this.calcTotal();
    }

    calcROI(): number {
        return this.calcNetIncome() / this.calcTotal();
    }

    calculateDownPayment() {
        const downPayment = this.calculator.getField(this.state.flipAnalysis_financeBox, 'down-payment').inputValue as number;
        const price = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'purchase-price').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(price) || this.calculator.isInputUnset(downPayment)) {
            return null;
        }

        return downPayment * price;
    }

    calculateAmountFinanced() {
        const financePercent = (1 - (this.calculator.getField(this.state.flipAnalysis_financeBox, 'down-payment').inputValue as number));
        const purchasePrice = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'purchase-price').inputValue as number;
        const rehabFinanced = this.calculator.getField(this.state.flipAnalysis_financeBox, 'rehab-financed').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(financePercent) || this.calculator.isInputUnset(purchasePrice) || this.calculator.isInputUnset(rehabFinanced)) {
            return null;
        }

        return (financePercent * purchasePrice) + rehabFinanced;
    }

    calculateAnnualLoanPayment() {
        const amountFinanced = this.calculateAmountFinanced() as number;
        const loanRate = this.calculator.getField(this.state.flipAnalysis_financeBox, 'loan-rate').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(loanRate)) {
            return null;
        }

        return amountFinanced * loanRate;
    }

    calculateLoanPointsPrivate() {
        const financed = this.calculateAmountFinanced() as number;
        const points = this.calculator.getField(this.state.flipAnalysis_financeBox, 'points').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(financed) || this.calculator.isInputUnset(points)) {
            return null;
        }

        return financed * points;
    }

    calculateMonthlyLoanPayment() {
        const payment = this.calculateAnnualLoanPayment() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(payment)) {
            return null;
        }

        return payment / 12;
    }

    calculateTotalPayment() {
        const monthly = this.calculateMonthlyLoanPayment() as number;
        const holdTime = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'hold-time').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(monthly) || this.calculator.isInputUnset(holdTime)) {
            return null;
        }

        return monthly * holdTime;
    }

    calculateHoldingPlusLoan() {
        const holdingCost = this.calcHoldingCost() as number;
        const totalLoanPayments = this.calculateTotalPayment() as number;
        const loanPointsPrivate = this.calculateLoanPointsPrivate() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(holdingCost) || this.calculator.isInputUnset(totalLoanPayments) || this.calculator.isInputUnset(loanPointsPrivate)) {
            return null;
        }

        return holdingCost + totalLoanPayments + loanPointsPrivate;
    }

    calculateTotalCashOutOfPocket() {
        const renoCost = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'reno-cost').inputValue as number;
        const rehabFinanced = this.calculator.getField(this.state.flipAnalysis_financeBox, 'rehab-financed').inputValue as number;
        const downPayment = this.calculateDownPayment() as number;
        const holdingAndLoan = this.calculateHoldingPlusLoan() as number;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(renoCost) ||
            this.calculator.isInputUnset(rehabFinanced) ||
            this.calculator.isInputUnset(downPayment) ||
            this.calculator.isInputUnset(holdingAndLoan)
        ) {
            return null;
        }

        return (renoCost - rehabFinanced) + downPayment + holdingAndLoan;
    }

    calculateNetIncomeAfterDebt() {
        const arv = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'arv').inputValue as number;
        const acqui = this.calcAcquiCost() as number;
        const dispo = this.calcDispo() as number;
        const holdingAndLoan = this.calculateHoldingPlusLoan() as number;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(arv) ||
            this.calculator.isInputUnset(acqui) ||
            this.calculator.isInputUnset(dispo) ||
            this.calculator.isInputUnset(holdingAndLoan)
        ) {
            return null;
        }

        return arv - acqui - dispo - holdingAndLoan;
    }

    calculateCashOnCash() {
        const net = this.calculateNetIncomeAfterDebt() as number;
        const outOfPockit = this.calculateTotalCashOutOfPocket() as number;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(net) ||
            this.calculator.isInputUnset(outOfPockit)
        ) {
            return null;
        }

        return net / outOfPockit;
    }

    calcInsuranceEstimateWhileHeld() {
        const monthlyInsuranceEstimate = (this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'arv').inputValue as number) * 0.00035;
        const holdTime = this.calculator.getField(this.state.flipAnalysis_sharedTopBox, 'hold-time').inputValue as number;
        return monthlyInsuranceEstimate * holdTime;
    }
}
